/**
 * 📦 DPD / Pickup-Anbindung (Frontend-Service)
 *
 * Verbindet PaketPilot direkt mit der Pickup-Schnittstelle (`com.ams.pudo` / GeoPost):
 *   1. Vorab-Namen (`SHIPPED` + `DELIVERED`): Empfängernamen sind schon vor dem Fahrer-Scan bekannt!
 *   2. Foto-Station & Handy: Beim Scannen eines 14-stelligen DPD-Barcodes (oder 28-stelligen DPD-Leitcodes)
 *      wird der Name sofort zugeordnet — und bei eingeschalteter Live-Annahme auch direkt in Pickup gebucht.
 *   3. Einstellungen (`DpdPanel`): 2-Schritt-Anmeldung per Shop-ID (z. B. NL24808) + E-Mail-Einmalcode,
 *      Tagesübersicht (Im Shop / Unterwegs / Rückgabe mit Fach), Bestands-Abgleich und Protokoll.
 */
import { apiFetch } from './apiService';
import { hasCustomFeature } from './customFeatures';

const SCHALTER_KEY = 'kiosk_dpd_enabled';
const ANNAHME_LIVE_KEY = 'kiosk_dpd_live_annahme';
const AUSGABE_LIVE_KEY = 'kiosk_dpd_live_ausgabe';

// ── Freischaltung & Schalter ─────────────────────────────────────────────────

export function dpdFreigeschaltet() {
  return hasCustomFeature('dpd');
}

export function isDpdOn() {
  try {
    return localStorage.getItem(SCHALTER_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdOn(on) {
  try {
    localStorage.setItem(SCHALTER_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
}

export function isDpdAnnahmeOn() {
  try {
    return localStorage.getItem(ANNAHME_LIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdAnnahmeOn(on) {
  try {
    localStorage.setItem(ANNAHME_LIVE_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
  dpdNotiz('live_schalter', 'annahme', true, '', on ? 'Live-Annahme eingeschaltet' : 'Live-Annahme ausgeschaltet');
}

export function isDpdAusgabeOn() {
  try {
    return localStorage.getItem(AUSGABE_LIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdAusgabeOn(on) {
  try {
    localStorage.setItem(AUSGABE_LIVE_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
  dpdNotiz('live_schalter', 'ausgabe', true, '', on ? 'Live-Ausgabe eingeschaltet' : 'Live-Ausgabe ausgeschaltet');
}

export function dpdAktiv() {
  return dpdFreigeschaltet() && isDpdOn();
}

export function dpdAnnahmeAktiv() {
  return dpdAktiv() && isDpdAnnahmeOn();
}

export function dpdAusgabeAktiv() {
  return dpdAktiv() && isDpdAusgabeOn();
}

// ── Barcode-Erkennung (14-stellige DPD-Paketnummer & 28-stelliger Leitcode) ──

/**
 * Extrahiert die 14-stellige DPD-Paketnummer aus einem Barcode oder Tracking-String.
 * - Klassischer DPD-Barcode: genau 14 Ziffern (z. B. `05162014839411` oder `15980141227691`)
 * - Langer DPD-2D/128-Leitcode (27–28 Zeichen, oft mit `%` am Anfang): Ziffern 8..21 sind die 14-stellige Paketnummer.
 */
export function dpdCodeAusTracking(trk) {
  const raw = String(trk || '').trim();
  if (!raw) return '';
  const clean = raw.replace(/^%+/, '').replace(/[\s-]+/g, '');
  if (/^\d{14}$/.test(clean)) return clean;
  if (/^\d{27,28}$/.test(clean)) {
    return clean.slice(7, 21);
  }
  return '';
}

const FREMDE_DIENSTE = new Set(['vinted', 'vinted go', 'dhl', 'gls', 'ups', 'hermes', 'mondial', 'mondial relay', 'postnl', 'bpost', 'amazon']);

/**
 * Prüft, ob ein Paket in PaketPilot ein DPD-Paket ist.
 */
export function istDpdPaket(p) {
  if (!p) return false;
  const dienst = String(p.paketdienst || p.dienst || p.carrier || '').trim().toLowerCase();
  if (dienst === 'dpd' || dienst === 'pickup' || dienst === 'chronopost') return true;
  if (dienst && FREMDE_DIENSTE.has(dienst)) return false;
  const rawTrk = String(p.trackingNumber || p.tracking || p.sendungsnummer || p.code || '').trim();
  const code = dpdCodeAusTracking(rawTrk);
  if (code && (bekannterDpdName(code) || rawTrk.replace(/\D/g, '').length >= 27)) return true;
  const hinweis = String(p.hinweis || p.notiz || p.note || '').toLowerCase();
  return hinweis.includes('dpd');
}

// ── Vorab geladene Namen (0 ms Nachschlagen beim Scannen) ────────────────────

const bekannteNamen = new Map(); // 14-stellige Nummer -> Empfängername
let letztesVorladenMs = 0;
let vorladenVersprechen = null;

export function merkeDpdName(codeOrTracking, name) {
  const n = String(name || '').trim();
  if (!n) return;
  const norm = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!norm) return;
  bekannteNamen.set(norm, n);
}

export function bekannterDpdName(codeOrTracking) {
  const norm = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!norm) return '';
  if (bekannteNamen.has(norm)) return bekannteNamen.get(norm) || '';
  // Falls nur die letzten Ziffern übergeben wurden
  if (norm.length >= 6 && norm.length < 14) {
    for (const [k, v] of bekannteNamen.entries()) {
      if (k.endsWith(norm)) return v;
    }
  }
  return '';
}

export function anzahlBekannteDpdNamen() {
  return bekannteNamen.size;
}

/**
 * Lädt alle aktuellen + angekündigten DPD-Pakete (`DELIVERED` + `SHIPPED`) vom Server
 * und legt deren Empfängernamen im Speicher ab.
 */
export async function vorladenStart(force = false) {
  if (!dpdAktiv()) return { ok: false, skipped: true };
  const jetzt = Date.now();
  if (!force && jetzt - letztesVorladenMs < 25000 && bekannteNamen.size > 0) {
    return { ok: true, cached: true, count: bekannteNamen.size };
  }
  if (vorladenVersprechen) return vorladenVersprechen;

  vorladenVersprechen = (async () => {
    try {
      const r = await api('/api/dpd/vorab-namen', { method: 'GET', timeoutMs: 12000 });
      if (r && r.ok && r.namen && typeof r.namen === 'object') {
        letztesVorladenMs = Date.now();
        for (const [nr, info] of Object.entries(r.namen)) {
          const name = typeof info === 'string' ? info : (info && info.name);
          if (name) merkeDpdName(nr, name);
        }
        return { ok: true, count: bekannteNamen.size, anzahl: r.anzahl || Object.keys(r.namen).length };
      }
      return { ok: false, reason: (r && r.reason) || 'fehler', message: (r && r.message) || '' };
    } catch (e) {
      return { ok: false, reason: 'netz', message: e.message };
    } finally {
      vorladenVersprechen = null;
    }
  })();

  return vorladenVersprechen;
}

// ── HTTP-Helfer (über apiFetch mit automatischem JWT-Refresh) ────────────────

async function api(pfad, { method = 'GET', body, timeoutMs = 20000 } = {}) {
  const cleanPath = pfad.replace(/^\/api/, '');
  try {
    return await apiFetch(cleanPath, {
      method,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      timeout: timeoutMs,
    });
  } catch (e) {
    if (e && e.status === 403) return { ok: false, reason: 'nicht_freigeschaltet', message: '' };
    return {
      ok: false,
      reason: e && e.zeitueberschreitung ? 'timeout' : 'offline',
      message: (e && e.message) || 'Netzwerkfehler',
      status: e && e.status,
      netzweg: !!(e && e.netzweg),
      zeitueberschreitung: !!(e && e.zeitueberschreitung),
    };
  }
}

// ── API-Aufrufe ──────────────────────────────────────────────────────────────

export function dpdStatus() {
  return api('/api/dpd/status', { method: 'GET', timeoutMs: 8000 });
}

export async function dpdUebersicht() {
  const r = await api('/api/dpd/uebersicht', { method: 'GET', timeoutMs: 15000 });
  // Wenn die Übersicht geladen wird, füllen wir nebenbei auch das Vorab-Namensgedächtnis
  vorladenStart(false).catch(() => {});
  return r;
}

export async function dpdBestand() {
  const r = await api('/api/dpd/bestand', { method: 'GET', timeoutMs: 15000 });
  if (r && r.ok && Array.isArray(r.pakete)) {
    for (const p of r.pakete) {
      const nr = p.code || p.parcelNumber;
      const nm = p.name || p.customerName;
      if (nr && nm) merkeDpdName(nr, nm);
    }
  }
  return r;
}

export function dpdRequestCode(pudoId) {
  return api('/api/dpd/request-code', {
    method: 'POST',
    body: { pudoId: String(pudoId || '').trim().toUpperCase() },
    timeoutMs: 15000,
  });
}

export function dpdVerifyCode(pudoId, code) {
  return api('/api/dpd/verify-code', {
    method: 'POST',
    body: {
      pudoId: String(pudoId || '').trim().toUpperCase(),
      code: String(code || '').trim().toUpperCase(),
    },
    timeoutMs: 25000,
  });
}

export async function dpdLogout() {
  bekannteNamen.clear();
  letztesVorladenMs = 0;
  return api('/api/dpd/logout', { method: 'POST', body: {}, timeoutMs: 10000 });
}

export async function dpdLookup(codeOrTracking) {
  const code = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!code) return { ok: false, reason: 'eingabe_fehlt' };
  const r = await api('/api/dpd/lookup', { method: 'POST', body: { code }, timeoutMs: 12000 });
  if (r && r.ok && r.found && r.name) {
    merkeDpdName(r.code || code, r.name);
  }
  return r;
}

export async function dpdAnnahme(codeOrTracking) {
  const code = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!code) return { ok: false, reason: 'eingabe_fehlt' };
  const r = await api('/api/dpd/intake', { method: 'POST', body: { code }, timeoutMs: 15000 });
  if (r && r.name) {
    merkeDpdName(r.code || code, r.name);
  }
  return r;
}

export function dpdAusgabe(codeOrTracking, { pin = '', pickerName = '', signature = '' } = {}) {
  const code = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!code) return Promise.resolve({ ok: false, reason: 'eingabe_fehlt' });
  return api('/api/dpd/pickup', {
    method: 'POST',
    body: { code, pin, pickerName, signature },
    timeoutMs: 15000,
  });
}

export function dpdAbgleich(pakete) {
  return api('/api/dpd/abgleich', {
    method: 'POST',
    body: { pakete: Array.isArray(pakete) ? pakete : [] },
    timeoutMs: 20000,
  });
}

export function dpdNotiz(op, code = '', ok = true, reason = '', message = '', detail = null) {
  if (!dpdFreigeschaltet()) return Promise.resolve({ ok: false });
  return api('/api/dpd/note', {
    method: 'POST',
    body: { op, code: String(code || '').slice(0, 40), ok: !!ok, reason, message, detail },
    timeoutMs: 5000,
  }).catch(() => ({ ok: false }));
}

export function dpdLog(limit = 80) {
  return api(`/api/dpd/log?limit=${ encodeURIComponent(limit) }`, { method: 'GET', timeoutMs: 10000 });
}

// ── Hilfsfunktionen für Abgleich & Rückgabe-Fächer ───────────────────────────

/**
 * Filtert aus allen PaketPilot-Paketen diejenigen heraus, die aktuell offen (nicht abgeholt)
 * und DPD zuzuordnen sind.
 */
export function offeneDpdPakete(packages = []) {
  const out = [];
  for (const p of packages || []) {
    if (!p || p.deleted || p.ausgegebenAm || p.zurueckgeschickt || p.abgeholt || p.pickedUp || p.status === 'picked_up') continue;
    if (!istDpdPaket(p)) continue;
    const trk = p.trackingNumber || p.tracking || p.sendungsnummer || p.code || '';
    const code = dpdCodeAusTracking(trk) || String(trk || '').trim();
    if (!code) continue;
    const name = String(p.name || p.empfaenger || `${p.vorname || ''} ${p.nachname || ''}`).trim();
    out.push({
      id: p.id,
      code,
      name,
      fach: p.regalplatz || p.fach || p.compartment || p.storageLocation || '',
    });
  }
  return out;
}

/**
 * Ordnet den abgelaufenen/vorzubereitenden Paketen aus DPD Pickup (`TO_PREPARE` / `PREPARED_FOR_COLLECTION`)
 * das passende Regalfach aus PaketPilot zu (über Paketnummer oder Empfängername).
 */
export function rueckgabeMitFach(rueckgabeListe = [], packages = []) {
  const offene = (packages || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && !p.abgeholt && !p.pickedUp);
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return (rueckgabeListe || []).map((r) => {
    const code = dpdCodeAusTracking(r.code) || String(r.code || '').trim();
    const rName = norm(r.name);
    let treffer = offene.find((p) => {
      const pCode = dpdCodeAusTracking(p.trackingNumber || p.tracking || p.sendungsnummer || '');
      return code && pCode && pCode === code;
    });
    if (!treffer && rName) {
      treffer = offene.find((p) => {
        const pName = norm(p.name || p.empfaenger || `${p.vorname || ''} ${p.nachname || ''}`);
        return pName && pName === rName;
      });
    }
    return {
      ...r,
      fach: (treffer && (treffer.regalplatz || treffer.fach || treffer.compartment || treffer.storageLocation)) || '',
    };
  });
}

// ── Mehrsprachige Texte (DE / NL / EN) ───────────────────────────────────────

const TEXTE = {
  de: {
    titel: 'DPD Pickup-Anbindung',
    untertitel: 'Direkte Anbindung an DPD / Pickup (Namen vorab, Annahme, Ausgabe & Abgleich)',
    verbunden: 'Mit DPD Pickup verbunden',
    nichtVerbunden: 'Nicht mit DPD Pickup verbunden',
    pudoIdLabel: 'Pickup Shop-ID (z. B. NL24808)',
    codeAnfordern: 'Code per E-Mail anfordern',
    codeSenden: 'Sende E-Mail…',
    codeGesendet: 'Ein 6-stelliger Code wurde an die Shop-E-Mail von DPD gesendet.',
    codeLabel: '6-stelliger E-Mail-Code',
    codeBestaetigen: 'Code bestätigen & verbinden',
    verbinde: 'Verbinde…',
    neuPruefen: 'Neu prüfen',
    abmelden: 'Abmelden',
    aufGeraetAktiv: 'Auf diesem Gerät eingeschaltet',
    nurLesenHinweis: 'Im Nur-Lesen-Modus schlägt PaketPilot beim Scannen von DPD-Paketen automatisch den Empfängernamen aus Pickup nach, ohne in DPD zu buchen.',
    liveAnnahme: 'Annahme vom Fahrer live in DPD buchen',
    liveAusgabe: 'Ausgabe an Kunden live in DPD buchen',
    heuteTitel: 'Heute bei DPD Pickup',
    imShop: 'Pakete im Shop',
    unterwegs: 'Unterwegs / angekündigt',
    rueckgabe: 'Rückgabe an Fahrer',
    abgleichStarten: 'Bestand mit DPD Pickup abgleichen',
    abgleichLaeuft: 'Vergleiche Bestand…',
    protokollTitel: 'Protokoll',
  },
  nl: {
    titel: 'DPD Pickup-koppeling',
    untertitel: 'Directe koppeling met DPD / Pickup (namen vooraf, inname, uitgifte & voorraadcontrole)',
    verbunden: 'Verbonden met DPD Pickup',
    nichtVerbunden: 'Niet verbonden met DPD Pickup',
    pudoIdLabel: 'Pickup Shop-ID (bijv. NL24808)',
    codeAnfordern: 'Code per e-mail aanvragen',
    codeSenden: 'E-mail versturen…',
    codeGesendet: 'Er is een 6-cijferige code naar het e-mailadres van de shop gestuurd.',
    codeLabel: '6-cijferige e-mailcode',
    codeBestaetigen: 'Code bevestigen & verbinden',
    verbinde: 'Verbinden…',
    neuPruefen: 'Opnieuw controleren',
    abmelden: 'Afmelden',
    aufGeraetAktiv: 'Ingeschakeld op dit apparaat',
    nurLesenHinweis: 'In alleen-lezen modus zoekt PaketPilot bij het scannen van DPD-pakketten automatisch de naam van de ontvanger op uit Pickup, zonder in DPD te boeken.',
    liveAnnahme: 'Inname van chauffeur direct in DPD boeken',
    liveAusgabe: 'Uitgifte aan klant direct in DPD boeken',
    heuteTitel: 'Vandaag bij DPD Pickup',
    imShop: 'Op voorraad in shop',
    unterwegs: 'Onderweg / aangekondigd',
    rueckgabe: 'Retour voor chauffeur',
    abgleichStarten: 'Voorraad vergelijken met DPD Pickup',
    abgleichLaeuft: 'Voorraad vergelijken…',
    protokollTitel: 'Logboek',
  },
  en: {
    titel: 'DPD Pickup Integration',
    untertitel: 'Direct connection to DPD / Pickup (recipient names in advance, intake, pickup & stock check)',
    verbunden: 'Connected to DPD Pickup',
    nichtVerbunden: 'Not connected to DPD Pickup',
    pudoIdLabel: 'Pickup Shop ID (e.g. NL24808)',
    codeAnfordern: 'Request code by email',
    codeSenden: 'Sending email…',
    codeGesendet: 'A 6-digit code has been sent to the shop email registered with DPD.',
    codeLabel: '6-digit email code',
    codeBestaetigen: 'Verify code & connect',
    verbinde: 'Connecting…',
    neuPruefen: 'Refresh',
    abmelden: 'Disconnect',
    aufGeraetAktiv: 'Enabled on this device',
    nurLesenHinweis: 'In read-only mode, PaketPilot automatically looks up the recipient name from DPD Pickup when scanning a DPD barcode, without booking in DPD.',
    liveAnnahme: 'Book driver intake live in DPD',
    liveAusgabe: 'Book customer pickup live in DPD',
    heuteTitel: 'Today at DPD Pickup',
    imShop: 'In shop inventory',
    unterwegs: 'On the way / announced',
    rueckgabe: 'Return to driver',
    abgleichStarten: 'Compare inventory with DPD Pickup',
    abgleichLaeuft: 'Comparing inventory…',
    protokollTitel: 'Activity log',
  },
};

export function dpdT(lang = 'de') {
  const k = String(lang || 'de').slice(0, 2).toLowerCase();
  return TEXTE[k] || TEXTE.de;
}
